import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useControllableProp,
  useFormControlContext,
} from '@chakra-ui/react';
import * as React from 'react';

import { SelectButton } from '@/components/SelectButton';
import { EAttributeAllowedEditorKind } from '@/graphql-types/globalTypes';
import { EAttributeOwnerKind } from '@/graphql-types/globalTypes';
import {
  BriefcaseIcon,
  ShieldIcon,
  UserIcon,
} from '@/imports/ui/chakra/feather';

export enum ALLOWED_EDITORS_LEVEL {
  USER_AND_ADMINS = 'USER_AND_ADMINS',
  ADMINS_ONLY = 'ADMINS_ONLY',
}

export const allowedEditorsLevelToLabel = (
  level: ALLOWED_EDITORS_LEVEL,
  ownerKind: EAttributeOwnerKind
) => {
  return {
    [ALLOWED_EDITORS_LEVEL.USER_AND_ADMINS]: {
      [EAttributeOwnerKind.USER]: 'The User & Admins',
      [EAttributeOwnerKind.ORGANIZATION]: `The Company's Admins & Association Admins`,
    }[ownerKind],
    [ALLOWED_EDITORS_LEVEL.ADMINS_ONLY]: {
      [EAttributeOwnerKind.USER]: 'Only Admins',
      [EAttributeOwnerKind.ORGANIZATION]: 'Only Association Admins',
    }[ownerKind],
  }[level];
};

const allowedEditorsLevelToIcon = (
  level: ALLOWED_EDITORS_LEVEL,
  ownerKind: EAttributeOwnerKind
) => {
  return {
    [ALLOWED_EDITORS_LEVEL.USER_AND_ADMINS]: {
      [EAttributeOwnerKind.USER]: <UserIcon />,
      [EAttributeOwnerKind.ORGANIZATION]: <BriefcaseIcon />,
    }[ownerKind],
    [ALLOWED_EDITORS_LEVEL.ADMINS_ONLY]: <ShieldIcon />,
  }[level];
};

export const allowedEditorsLevelToValue = (level: ALLOWED_EDITORS_LEVEL) => {
  return {
    [ALLOWED_EDITORS_LEVEL.USER_AND_ADMINS]: [EAttributeAllowedEditorKind.SELF],
    [ALLOWED_EDITORS_LEVEL.ADMINS_ONLY]: [],
  }[level];
};

export const allowedEditorsValueToLevel = (
  value: EAttributeAllowedEditorKind[]
) => {
  if (value.includes(EAttributeAllowedEditorKind.SELF)) {
    return ALLOWED_EDITORS_LEVEL.USER_AND_ADMINS;
  }

  return ALLOWED_EDITORS_LEVEL.ADMINS_ONLY;
};

export const AllowedEditorSelect = React.forwardRef<
  HTMLButtonElement,
  {
    value: EAttributeAllowedEditorKind[];
    onChange: (value: EAttributeAllowedEditorKind[]) => void;
    isDisabled?: boolean;
    ownerKind: EAttributeOwnerKind;
  }
>((props, ref) => {
  const currentLevel = allowedEditorsValueToLevel(props.value);

  const formControlContext = useFormControlContext();

  const [, isDisabled] = useControllableProp(
    props.isDisabled ? true : undefined,
    formControlContext?.isDisabled
  );

  return (
    <Menu matchWidth isLazy>
      <MenuButton
        as={SelectButton}
        ref={ref}
        leftIcon={allowedEditorsLevelToIcon(currentLevel, props.ownerKind)}
        isDisabled={isDisabled}
      >
        {allowedEditorsLevelToLabel(currentLevel, props.ownerKind)}
      </MenuButton>
      <MenuList>
        <MenuItem
          icon={allowedEditorsLevelToIcon(
            ALLOWED_EDITORS_LEVEL.USER_AND_ADMINS,
            props.ownerKind
          )}
          onClick={() =>
            props.onChange(
              allowedEditorsLevelToValue(ALLOWED_EDITORS_LEVEL.USER_AND_ADMINS)
            )
          }
        >
          {allowedEditorsLevelToLabel(
            ALLOWED_EDITORS_LEVEL.USER_AND_ADMINS,
            props.ownerKind
          )}
        </MenuItem>
        <MenuItem
          icon={allowedEditorsLevelToIcon(
            ALLOWED_EDITORS_LEVEL.ADMINS_ONLY,
            props.ownerKind
          )}
          onClick={() =>
            props.onChange(
              allowedEditorsLevelToValue(ALLOWED_EDITORS_LEVEL.ADMINS_ONLY)
            )
          }
        >
          {allowedEditorsLevelToLabel(
            ALLOWED_EDITORS_LEVEL.ADMINS_ONLY,
            props.ownerKind
          )}
        </MenuItem>
      </MenuList>
    </Menu>
  );
});

AllowedEditorSelect.displayName = 'AllowedEditorSelect';
